import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiGlobe, FiTrendingUp, FiShield, FiStar, FiArrowRight, FiCheckCircle, FiScale } = FiIcons;

function Home() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { icon: FiAward, number: '25+', label: 'Years of Experience' },
    { icon: FiUsers, number: '500+', label: 'Successful Cases' },
    { icon: FiGlobe, number: '8', label: 'Expert Attorneys' },
    { icon: FiTrendingUp, number: '3', label: 'Languages Spoken' }
  ];

  const services = [
    {
      icon: FiShield,
      title: 'Corporate Law',
      description: 'Comprehensive business legal services from formation to complex M&A transactions.',
      features: ['Company Formation', 'Corporate Governance', 'M&A Advisory', 'Compliance']
    },
    {
      icon: FiUsers,
      title: 'Employment Law',
      description: 'Expert guidance on labor relations, employment equity, and workplace compliance.',
      features: ['Employment Contracts', 'BBBEE Compliance', 'Labor Disputes', 'HR Policies']
    },
    {
      icon: FiScale,
      title: 'Commercial Litigation',
      description: 'Strategic dispute resolution through negotiation, mediation, and court proceedings.',
      features: ['Contract Disputes', 'Debt Recovery', 'Alternative Resolution', 'Court Representation']
    }
  ];

  const testimonials = [
    {
      text: "Oliphant TCZ & Associates handled our company's restructuring with exceptional skill and attention to detail. Their guidance was invaluable during a challenging period.",
      author: "CEO, Manufacturing Company",
      location: "Johannesburg"
    },
    {
      text: "The employment law expertise at this firm is outstanding. They helped us navigate complex labor relations issues with professionalism and care.",
      author: "HR Director",
      location: "Mining Corporation"
    },
    {
      text: "Advocate Oliphant's litigation skills are remarkable. He achieved a settlement that exceeded our expectations while maintaining the highest ethical standards.",
      author: "Managing Director",
      location: "Technology Firm"
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
                Trusted Legal Excellence
              </h1>
              <p className="font-opensans text-xl text-gray-200 mb-8 leading-relaxed">
                Over 25 years of dedicated legal service to corporations and individuals across Gauteng. We combine deep legal expertise with a commitment to understanding our clients' unique needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200 text-center"
                >
                  Schedule Your Consultation
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-white hover:text-navy-800 transition-colors duration-200 text-center"
                >
                  Our Services
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80"
                alt="Johannesburg Skyline"
                className="rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-4">
                  <SafeIcon icon={stat.icon} className="w-8 h-8" />
                </div>
                <div className="font-opensans font-bold text-3xl text-navy-800 mb-2">
                  {stat.number}
                </div>
                <div className="font-opensans text-gray-600">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-opensans font-bold text-4xl text-navy-800 mb-4">
              Our Legal Services
            </h2>
            <p className="font-opensans text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive legal solutions tailored to meet the diverse needs of businesses and individuals across South Africa.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8" />
                </div>
                <h3 className="font-opensans font-bold text-2xl text-navy-800 mb-4">
                  {service.title}
                </h3>
                <p className="font-opensans text-gray-600 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-gold-600" />
                      <span className="font-opensans text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  className="inline-flex items-center text-gold-600 font-opensans font-semibold hover:text-gold-700 transition-colors"
                >
                  Learn More
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-opensans font-bold text-4xl text-navy-800 mb-4">
              What Our Clients Say
            </h2>
            <p className="font-opensans text-xl text-gray-600">
              Trusted by businesses and individuals across Gauteng
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-gold-600 fill-current" />
                  ))}
                </div>
                <p className="font-opensans text-gray-700 mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-opensans font-semibold text-navy-800">
                    {testimonial.author}
                  </p>
                  <p className="font-opensans text-sm text-gray-600">
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-opensans font-bold text-4xl mb-4">
            Ready to Get Started?
          </h2>
          <p className="font-opensans text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact us today for a consultation and discover how our legal expertise can help protect and advance your interests.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/attorneys"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-white hover:text-navy-800 transition-colors duration-200"
            >
              Meet Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;